import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { ModuleExtendedClient } from '@frontend/module/api';
import { moduleRequest } from '@frontend/module/events';
import { Module, ModuleExtended } from '@frontend/module/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useModuleDetailModal } from '../../applications/modals/module-detail-modal/module-detail-modal.controller';
import {
    ModuleActivitySection,
    ModuleConstraintsSection,
    ModuleContentsSection,
    ModuleGeneralSection
} from '../../applications/modals/module-detail-modal/module-detail-sections';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';

const ModuleDetailsCard = ({ module }: { module: ModuleExtended }) => (
    <div className='bg-background-surface outline-border-default mb-4 inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={module}
            cols={3}
            order={['spot', 'index', 'type', 'num_slots', 'creation_timestamp', 'update_timestamp']}
            keyOverwrite={
                new Map([
                    ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                    ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                    ['index', CommonMessage.OBJECTS.MODULE.DEFAULT],
                    ['num_slots', CommonMessage.OBJECTS.SLOT.PLURAL]
                ])
            }
            valueOverwrite={
                new Map([
                    [
                        'spot',
                        (obj: ModuleExtended) => (
                            <PlatformObjectBadge
                                id={obj.spot?.entity_id}
                                display={obj.spot?.name}
                                entityType={EntityType.SPOT}
                                foreign={obj.spot?.foreign}
                                accountId={accountIdFromResolvedEntity(obj.spot)}
                            />
                        )
                    ],
                    [
                        'type',
                        (obj: ModuleExtended) => (
                            <DefaultBadge
                                styles={{ container: { color: 'bg-brand-muted' } }}
                                label={obj.type ? (obj.type.charAt(0).toUpperCase() + obj.type.slice(1)).replace(/_/g, ' ') : '/'}
                            />
                        )
                    ]
                ])
            }
        />
    </div>
);

/** Hooks live here so registry `render` callbacks stay free of hooks. */
const ModuleReactiveTab = ({ view, tab }: { view: ReactiveDetailView<Module, ModuleExtended>; tab: 'general' | 'activity' | 'contents' | 'constraints' }) => {
    const actions = useModuleDetailModal();
    const module = view.extended;
    if (!module) return null;

    if (tab === 'general') {
        return (
            <>
                <ModuleDetailsCard module={module} />
                <ModuleGeneralSection module={module} />
            </>
        );
    }
    if (tab === 'activity') {
        return (
            <ModuleActivitySection
                module={module}
                dispatch={actions.dispatch}
                viewDetailedConstraintsList={actions.viewDetailedConstraintsList}
            />
        );
    }
    if (tab === 'contents') {
        return (
            <ModuleContentsSection
                module={module}
                dispatch={actions.dispatch}
                viewDetailedConstraintsList={actions.viewDetailedConstraintsList}
            />
        );
    }
    return (
        <ModuleConstraintsSection
            module={module}
            dispatch={actions.dispatch}
            viewDetailedConstraintsList={actions.viewDetailedConstraintsList}
        />
    );
};

export const moduleReactiveDetail: ReactiveDetailRegistryEntry<Module, ModuleExtended> = {
    entityType: EntityType.MODULE,
    resolveFunction: moduleRequest,
    fetchExtended: ({ object, parents }) => ModuleExtendedClient.fetchModuleExtended(object.account_id, parents.spot_id ?? object.spot_id, object.id),
    title: ({ extended, object }) => (
        <>
            {CommonMessage.OBJECTS.MODULE.DEFAULT}: {extended?.name ?? object.name ?? extended?.index ?? object.index}
        </>
    ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (
                <ModuleReactiveTab
                    view={view}
                    tab='general'
                />
            )
        },
        {
            id: 'activity',
            label: (
                <FormattedMessage
                    id='platform.applications.modals.module-detail-modal.activity-tab'
                    description='Tab label for module activity (transactions) on the MODULE detail modal'
                    defaultMessage='Activity'
                />
            ),
            render: (view) => (
                <ModuleReactiveTab
                    view={view}
                    tab='activity'
                />
            )
        },
        {
            id: 'contents',
            label: CommonMessage.OBJECTS.STOCK.DEFAULT,
            render: (view) => (
                <ModuleReactiveTab
                    view={view}
                    tab='contents'
                />
            )
        },
        {
            id: 'constraints',
            label: CommonMessage.OBJECTS.CONSTRAINT.PLURAL,
            render: (view) => (
                <ModuleReactiveTab
                    view={view}
                    tab='constraints'
                />
            )
        }
    ]
};
